// src/main.ts
import 'hammerjs';

// NG0912 (Component ID generation collision) ist ein bekannter Angular-19-
// Hinweis bei Standalone-Material-Komponenten in mehreren lazy-loaded
// Chunks. Betrifft nur SSR/Hydration, nicht unseren CSR-Workflow. Wir
// unterdrücken die Warning, andere Warnings bleiben sichtbar.
(() => {
  const originalWarn = console.warn.bind(console);
  console.warn = (...args: unknown[]) => {
    const first = args[0];
    if (typeof first === 'string' && first.includes('NG0912')) return;
    originalWarn(...args);
  };
})();

import { bootstrapApplication } from '@angular/platform-browser';
import { importProvidersFrom, LOCALE_ID } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
  HttpClient,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';

import {
  HAMMER_GESTURE_CONFIG,
  HammerModule,
  HammerGestureConfig,
} from '@angular/platform-browser';
import * as Hammer from 'hammerjs';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';

import { jwtInterceptor } from './app/auth/jwt.interceptor';
import { authErrorInterceptorFn } from './app/auth/auth-error.interceptor';
import { LanguageInterceptor } from './app/interceptors/language.interceptor';

// ⬇️ neu/re-aktiviert: hängt ?user_id an API-Calls
import { athleteParamInterceptorFn } from './app/shared/athlete-param.interceptor';
// ⬇️ hängt X-Active-Workspace-Id an API-Calls (geteilte Spezialtool-Daten)
import { activeWorkspaceInterceptorFn } from './app/shared/active-workspace.interceptor';

import { NgxEchartsModule } from 'ngx-echarts';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';

// Zentraler deutscher DateAdapter (parst dd.MM.yyyy korrekt statt US-mm.dd via
// Date.parse). Ersetzt MatNativeDateModule app-weit.
import { provideDeDateAdapter } from './app/shared/date/de-date.adapter';

// i18n
import { TranslateModule, TranslateLoader, MissingTranslationHandler } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HumanizeMissingTranslationHandler } from './app/shared/i18n-missing-translation.handler';

// Locale-Registration
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
registerLocaleData(localeDe);

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// HammerJS Config
export class MyHammerConfig extends HammerGestureConfig {
  override overrides = { swipe: { direction: Hammer.DIRECTION_HORIZONTAL } };
}

// Font-Scale: restore persisted user preference before first render
const fontScale = localStorage.getItem('fontScale') ?? '1';
document.documentElement.style.setProperty('--font-scale', fontScale);

// Theme: restore persisted user preference before first render (FOUC-Vermeidung)
const storedTheme = localStorage.getItem('theme');
if (storedTheme === 'dark') {
  document.documentElement.classList.add('dark');
}

bootstrapApplication(AppComponent, {
  providers: [
    // Kein PreloadAllModules — sonst werden alle 89 lazy-Chunks direkt nach
    // Bootstrap nachgeladen und Lazy-Loading ist effektiv ausgeschaltet.
    provideRouter(appRoutes),
    provideAnimations(),

    // HttpClient + Interceptors
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([
        jwtInterceptor,            // Auth
        authErrorInterceptorFn,    // ⬅️ 401 (abgelaufen/widerrufen) → Logout + /login
        athleteParamInterceptorFn, // ⬅️ fügt ?user_id basierend auf CurrentAthleteService hinzu
        activeWorkspaceInterceptorFn // ⬅️ fügt X-Active-Workspace-Id hinzu
      ]),
    ),
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },

    // Module via importProvidersFrom
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: { provide: TranslateLoader, useFactory: HttpLoaderFactory, deps: [HttpClient] },
        // fr/es/pt/pl sind lueckenhaft — ohne Handler landet der rohe Schluessel
        // im Layout und sprengt es (siehe i18n-missing-translation.handler.ts).
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: HumanizeMissingTranslationHandler,
        },
      }),
      // echarts dynamisch importieren — erst geladen wenn eine Chart-Komponente
      // gerendert wird (statt im main-Bundle eingebacken: ca. 1 MB Ersparnis).
      NgxEchartsModule.forRoot({ echarts: () => import('echarts') }),
      HammerModule,
      MatSnackBarModule,
    ),

    // Tokens
    { provide: LOCALE_ID, useValue: 'de' },
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    // App-weiter DateAdapter + Formate (DateAdapter + MAT_DATE_FORMATS). Ersetzt
    // die von MatNativeDateModule gestellten Bindings — daher darf keine
    // Datepicker-Komponente MatNativeDateModule mehr importieren (sonst
    // überschattet deren Element-Injector diese Root-Provider).
    ...provideDeDateAdapter(),
    { provide: HAMMER_GESTURE_CONFIG, useClass: MyHammerConfig },
    // Alle <mat-icon> verwenden standardmäßig Material Symbols Outlined
    // (statt der klassischen Material Icons Filled).
    { provide: MAT_ICON_DEFAULT_OPTIONS, useValue: { fontSet: 'material-symbols-outlined' } },
  ],
}).catch((err) => console.error(err));
